import { useMemo } from 'react'

import { ROUTES } from '@/app/router/paths'
import { isSectionInMenu } from '@/app/router/sections'
import { useVehiclesSummary } from '@/pages/cars/hooks/useVehicles'
import {
  useQueuesHistoryQuery,
  useQueuesQuery,
  useVisitorsQuery,
} from '@/pages/clients/hooks/useClientsData'
import { useEventsPageQuery } from '@/pages/events/hooks/useEventsQuery'
import { apiValueNumber } from '@/services/api/schemas/common'
import { queueAverageText } from '@/services/api/schemas/queues'
import { usePeriod } from '@/services/period/usePeriod'
import { useCashiersQuery, useColumnsQuery } from '@/services/station/queries'
import { plural } from '@/shared/lib/format'
import { PERIOD_GENITIVE } from '@/shared/lib/period'
import grid from '@/shared/styles/layout.module.css'
import { KpiCard } from '@/shared/ui'

import { CashiersPanel } from './components/CashiersPanel'
import { ColumnsPanel } from './components/ColumnsPanel'
import { RecentEvents } from './components/RecentEvents'
import s from './HomePage.module.css'

/**
 * Главная: в ряд «Колонки», «Кассиры» и сетка 2×2 KPI («Посетители», «Очереди на кассах», «Автомобили», «События»),
 * ниже «Последние события». Всё — реальный API: колонки и кассиры — текущее состояние без учёта
 * периода (ТЗ п. 3.3), очередь сейчас — тоже текущее состояние, остальное — за период. Все четыре KPI
 * из ТЗ п. 4.1 вернулись по мере появления данных на бэкенде (последний, «Очереди на кассах», — 18.09.2026).
 */
export function HomePage() {
  const { period, rangeParams } = usePeriod()
  const columns = useColumnsQuery()
  const cashiers = useCashiersQuery()
  const range = useMemo(
    () => ({ from: rangeParams.from, to: rangeParams.to }),
    [rangeParams.from, rangeParams.to],
  )
  const visitors = useVisitorsQuery(range)
  const queues = useQueuesQuery()
  const queuesHistory = useQueuesHistoryQuery(range)
  const vehicles = useVehiclesSummary(range)
  const periodEvents = useEventsPageQuery(useMemo(() => ({ ...range, limit: 1 }), [range]))
  const recent = useEventsPageQuery(useMemo(() => ({ limit: 5 }), []))
  const eventsTotal = periodEvents.data?.total
  // Число есть только при status ok, иначе «Нет данных»; ноль показывается как «0 авто» (ТЗ п. 4.1, 8).
  const carVisits = apiValueNumber(vehicles.data?.total_visits)
  const visitorsTotal = apiValueNumber(visitors.data?.total)
  // «Очереди на кассах» (ТЗ п. 4.1): сейчас — из /queues, среднее за период — из /queues/history.
  // Сноску «данные по N из M касс» заказчик убрал 23.09.2026, как и в «Потоке посетителей».
  const queueNow = apiValueNumber(queues.data?.total)
  const queueAvg = queueAverageText(queuesHistory.data?.average)

  return (
    <div className={grid.grid}>
      <ColumnsPanel
        columns={columns.data}
        isLoading={columns.isPending}
        error={columns.error}
        onRetry={() => void columns.refetch()}
        className={s.columns}
      />
      <CashiersPanel
        cashiers={cashiers.data}
        isLoading={cashiers.isPending}
        error={cashiers.error}
        onRetry={() => void cashiers.refetch()}
        className={s.cashiers}
      />
      <div className={s.kpis}>
        {/* Переходы в разделы — только когда раздел в меню (ТЗ п. 9): по прямому URL он без ссылок. */}
        <KpiCard
          size="sm"
          label="Посетители"
          value={visitorsTotal}
          unit={visitorsTotal !== undefined ? 'чел.' : undefined}
          placeholder={visitorsTotal === undefined ? 'Нет данных' : undefined}
          sub={`вошли в здание ${PERIOD_GENITIVE[period]}`}
          to={isSectionInMenu('clients') ? ROUTES.clients : undefined}
          loading={visitors.isPending}
        />
        <KpiCard
          size="sm"
          label="Очереди на кассах"
          value={queueNow}
          unit={queueNow !== undefined ? 'чел.' : undefined}
          placeholder={queueNow === undefined ? 'Нет актуальных данных' : undefined}
          sub={
            queuesHistory.isPending
              ? ' '
              : queueAvg !== undefined
                ? `в среднем ${queueAvg} чел. ${PERIOD_GENITIVE[period]}`
                : 'Нет данных за выбранный период'
          }
          to={isSectionInMenu('clients') ? ROUTES.clients : undefined}
          loading={queues.isPending}
        />
        <KpiCard
          size="sm"
          label="Автомобили"
          value={carVisits}
          unit={carVisits !== undefined ? 'авто' : undefined}
          placeholder={carVisits === undefined ? 'Нет данных' : undefined}
          sub={`посещений ${PERIOD_GENITIVE[period]}`}
          to={isSectionInMenu('cars') ? ROUTES.cars : undefined}
          loading={vehicles.isPending}
        />
        <KpiCard
          size="sm"
          label="События"
          value={eventsTotal}
          unit={
            eventsTotal !== undefined
              ? plural(eventsTotal, 'событие', 'события', 'событий')
              : undefined
          }
          placeholder={eventsTotal === undefined ? 'Нет данных' : undefined}
          sub={`зарегистрировано ${PERIOD_GENITIVE[period]}`}
          to={ROUTES.events}
          loading={periodEvents.isPending}
        />
      </div>
      <RecentEvents
        page={recent.data}
        isLoading={recent.isPending}
        error={recent.error}
        onRetry={() => void recent.refetch()}
        className={grid.c12}
      />
    </div>
  )
}
